import type React from 'react'
import { useEffect, useRef, useState } from 'react'

interface SafeHtmlRendererProps {
	htmlContent: string
	className?: string
	maxHeight?: string
}

export const SafeHtmlRenderer: React.FC<SafeHtmlRendererProps> = ({
	htmlContent,
	className = '',
	maxHeight = 'none',
}) => {
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const [iframeHeight, setIframeHeight] = useState<string>('0px')
	const resizeObserverRef = useRef<ResizeObserver | null>(null)

	useEffect(() => {
		const updateIframeContent = (): void => {
			const iframe = iframeRef.current
			if (!iframe) return

			const iframeWindow = iframe.contentWindow
			if (!iframeWindow) return

			const iframeDoc = iframe.contentDocument || iframeWindow.document
			if (!iframeDoc) return

			while (iframeDoc.body.firstChild) {
				iframeDoc.body.removeChild(iframeDoc.body.firstChild)
			}

			const styleElement = iframeDoc.createElement('style')
			styleElement.textContent = `
        html, body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          margin: 0;
          padding: 0;
          overflow: hidden; /* Prevent scrolling within the iframe */
          height: auto;
        }
        
        /* Hide scrollbars cross-browser */
        html::-webkit-scrollbar, body::-webkit-scrollbar {
          display: none;
        }
        
        html {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
      `
			iframeDoc.head.appendChild(styleElement)

			const container = iframeDoc.createElement('div')
			container.innerHTML = htmlContent
			iframeDoc.body.appendChild(container)

			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect()
			}

			resizeObserverRef.current = new ResizeObserver(() => {
				adjustIframeHeight()
			})

			resizeObserverRef.current.observe(iframeDoc.body)
			resizeObserverRef.current.observe(container)

			adjustIframeHeight()
		}

		const adjustIframeHeight = (): void => {
			const iframe = iframeRef.current
			if (!iframe?.contentDocument?.body) return

			const height = iframe.contentDocument.body.scrollHeight
			setIframeHeight(`${height}px`)
		}

		const iframe = iframeRef.current
		if (iframe) {
			iframe.onload = updateIframeContent

			if (iframe.contentDocument?.readyState === 'complete') {
				updateIframeContent()
			}
		}

		window.addEventListener('resize', adjustIframeHeight)

		return () => {
			if (iframe) {
				iframe.onload = null
			}

			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect()
				resizeObserverRef.current = null
			}

			window.removeEventListener('resize', adjustIframeHeight)
		}
	}, [htmlContent])

	return (
		<iframe
			ref={iframeRef}
			title='Rendered HTML Content'
			className={className}
			style={{
				width: '100%',
				height: iframeHeight,
				maxHeight: maxHeight,
				overflow: 'hidden',
				transition: 'height 0.2s ease',
			}}
			sandbox='allow-same-origin'
			srcDoc='<!DOCTYPE html><html><head></head><body></body></html>'
		/>
	)
}
